/**
 * GlassCard + GlassPanel + GlassHero — surface-tier wrappers.
 *
 * Use GlassCard for primary content tiles (metric modules, result rows).
 * Use GlassPanel for larger frosted surfaces (sidebars, drawers, toolbars).
 * Use GlassHero for the focal intelligence card (Analyze HQ score).
 *
 * All three share: soft inner top highlight, translucent fill,
 * 1px glass stroke, soft depth shadow. Hover lift is opt-in via `hoverLift`.
 */
import React from 'react';

export function GlassCard({
  children, className = '', hoverLift = false, as: Tag = 'div', ...rest
}) {
  return (
    <Tag
      className={`liquid-card ${hoverLift ? 'liquid-lift' : ''} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function GlassPanel({
  children, className = '', as: Tag = 'div', ...rest
}) {
  return (
    <Tag className={`liquid-panel ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

export function GlassHero({
  children, className = '', as: Tag = 'div', ...rest
}) {
  return (
    <Tag className={`liquid-card-hero ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * GlassPill — semantic status/state chip. Variant controls tint.
 */
export function GlassPill({
  children, variant = 'default', live = false, className = '', ...rest
}) {
  const variantClass = {
    default: '',
    success: 'liquid-pill--success',
    warning: 'liquid-pill--warning',
    danger:  'liquid-pill--danger',
    info:    'liquid-pill--info',
  }[variant] || '';
  return (
    <span
      className={`liquid-pill ${variantClass} ${live ? 'liquid-pill--live' : ''} ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

/**
 * GlassSegmentedControl — sliding pill tabs.
 * Controlled component: pass `items`, `value`, `onChange`.
 */
export function GlassSegmentedControl({
  items, value, onChange, className = '', testIdPrefix = 'segmented',
}) {
  return (
    <div className={`liquid-segmented ${className}`} role="tablist" data-testid={`${testIdPrefix}-control`}>
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            data-active={active ? 'true' : 'false'}
            onClick={() => onChange?.(item.value)}
            className="liquid-segmented__item"
            data-testid={`${testIdPrefix}-${item.value}`}
          >
            {item.icon && React.isValidElement(item.icon)
              ? React.cloneElement(item.icon, { className: 'w-3.5 h-3.5' })
              : item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * GlassButton — luminous CTA or ghost action.
 */
export function GlassButton({
  children, variant = 'default', className = '', as: Tag = 'button', ...rest
}) {
  const variantClass = {
    default: '',
    primary: 'liquid-btn--primary',
  }[variant] || '';
  return (
    <Tag
      className={`liquid-btn ${variantClass} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}
